import { useState } from "react";
import { Icon } from "@iconify/react";
import { enqueueSnackbar } from "notistack";
import Modal1 from "../../../components/DIsplay/Modal/Modal1";
import Button1 from "../../../components/form/Button1";
import { formatMoney } from "../../../features/utils/formatMoney";
import { getWorldCupGames, settleWorldCup } from "./worldCupController";
import {
  getWorldCupTeamFlagIcon,
  saveWorldCupGames,
} from "./payload";

export default function ConfirmSettleWinnerModal({
  open,
  setOpen,
  gameId,
  game,
  selectedTeam,
  onSuccess,
}) {
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    if (!gameId) {
      enqueueSnackbar("Unable to settle game. Game ID is missing.", {
        variant: "error",
      });
      return;
    }

    if (!selectedTeam) {
      enqueueSnackbar("Please select the winning team", { variant: "error" });
      return;
    }

    setLoading(true);
    const res = await settleWorldCup(gameId, { winningTeam: selectedTeam });
    setLoading(false);

    if (res.return) {
      enqueueSnackbar(res.msg, { variant: "success" });
      const gamesRes = await getWorldCupGames({ limit: 10, offset: 0 });
      if (gamesRes.return) {
        const nextGames = Array.isArray(gamesRes.data?.data)
          ? gamesRes.data.data
          : [];
        saveWorldCupGames(nextGames);
      } else {
        saveWorldCupGames([]);
      }
      setOpen(false);
      onSuccess?.(res.data);
    } else {
      enqueueSnackbar(res.msg, { variant: "error" });
    }
  };

  return (
    <Modal1
      open={open}
      setOpen={setOpen}
      modalBorderRadius="rounded-2xl"
      config={{ bg: false, hideMobileClose: true }}
    >
      <div className="w-[min(100vw-2rem,440px)] overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="relative overflow-hidden bg-gradient-to-br from-[#7C2D12] via-[#C2410C] to-[#EA580C] px-6 pb-8 pt-6 text-white">
          <div className="pointer-events-none absolute -right-6 -top-6 h-28 w-28 rounded-full bg-white/10" />
          <div className="pointer-events-none absolute -bottom-10 -left-4 h-24 w-24 rounded-full bg-white/10" />

          <button
            type="button"
            className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-white transition hover:bg-white/25"
            onClick={() => setOpen(false)}
            aria-label="Close"
          >
            <Icon icon="mdi:close" className="text-lg" />
          </button>

          <div className="flex flex-col items-center gap-3 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/20 backdrop-blur-sm">
              <Icon icon="mdi:trophy" className="text-3xl" />
            </span>
            <div className="flex flex-col gap-1">
              <span className="text-xl font-bold tracking-tight">
                Confirm Winner
              </span>
              <span className="text-sm text-white/80">
                Make sure you have selected the correct winning nation.
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-5 px-6 py-6">
          <div className="flex flex-col items-center gap-4 rounded-2xl border-2 border-amber-200 bg-gradient-to-b from-amber-50 to-white p-6 text-center shadow-sm">
            <Icon
              icon={getWorldCupTeamFlagIcon(selectedTeam)}
              className="h-16 w-16 rounded-full"
            />
            <div className="flex flex-col gap-1">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-700">
                Selected Winner
              </span>
              <span className="text-2xl font-extrabold text-[#0B3D91]">
                {selectedTeam}
              </span>
            </div>
            <span className="text-sm text-[#6D7C94]">
              Prize pool:{" "}
              <span className="font-semibold text-primary">
                {formatMoney(game?.prizePoolAmount)}
              </span>
            </span>
          </div>

          <div className="flex items-start gap-3 rounded-xl border border-red-100 bg-red-50/80 px-4 py-3">
            <Icon
              icon="mdi:alert-circle-outline"
              className="mt-0.5 shrink-0 text-lg text-red-600"
            />
            <span className="text-sm leading-relaxed text-red-900">
              Settling the game will end the tournament and process payouts for{" "}
              <span className="font-semibold">{selectedTeam}</span>. This
              action cannot be undone.
            </span>
          </div>

          <div className="flex gap-3 pt-1">
            <Button1
              variant="outlined"
              type="button"
              className="flex-1"
              onClick={() => setOpen(false)}
            >
              Go Back
            </Button1>
            <Button1
              type="button"
              className="flex-1 !bg-[#C2410C] hover:!bg-[#9A3412]"
              loading={loading}
              onClick={handleConfirm}
            >
              Confirm Settle
            </Button1>
          </div>
        </div>
      </div>
    </Modal1>
  );
}
